import {
  registerDecorator,
  ValidationArguments,
  ValidationOptions,
} from 'class-validator';
import { customAlphabet } from 'nanoid';
import { CnxIdOptions, CnxIdType } from './types';

const defaultOptions: CnxIdOptions = {
  alphabet: '0123456789abcdefghijklmnopqrstwxyzABCDEFGHIJKLMNOPQRSTWXYZ',
  size: 20,
  prefix: '',
  time_size: 7, // NOTE: if >0 uses this many chars to represent the current time (makes id sortable by creation time)
};

export const cnxIdTypeOptions: Record<CnxIdType, CnxIdOptions> = {
  [CnxIdType.Generic]: {
    ...defaultOptions,
  },
  [CnxIdType.Random]: {
    ...defaultOptions,
    size: 20,
    prefix: '',
    time_size: 0, // NOTE: make it truly random, not sortable
  },
  [CnxIdType.RandomShort]: {
    ...defaultOptions,
    size: 10,
    prefix: '',
    time_size: 0,
  },
  [CnxIdType.User]: {
    ...defaultOptions,
    prefix: 'UsR',
  },
  [CnxIdType.Tenant]: {
    ...defaultOptions,
    prefix: 'tNt',
  },
  [CnxIdType.Company]: {
    ...defaultOptions,
    prefix: 'cmP',
  },
  [CnxIdType.Location]: {
    ...defaultOptions,
    prefix: 'Loc',
  },
  [CnxIdType.Load]: {
    ...defaultOptions,
    prefix: 'loD',
  },
  [CnxIdType.Vehicle]: {
    ...defaultOptions,
    prefix: 'vHc',
  },
  [CnxIdType.Fleet]: {
    ...defaultOptions,
    prefix: 'fEt',
  },
  [CnxIdType.TourPlanFleet]: {
    ...defaultOptions,
    prefix: 'fTp',
  },
  [CnxIdType.Tourplan]: {
    ...defaultOptions,
    prefix: 'tPn',
  },
  [CnxIdType.Tour]: {
    ...defaultOptions,
    prefix: 'tOu',
  },
  [CnxIdType.Stop]: {
    ...defaultOptions,
    prefix: 'Stp',
  },
  [CnxIdType.Shipment]: {
    ...defaultOptions,
    prefix: 'SpM',
  },
  [CnxIdType.Shipper]: {
    ...defaultOptions,
    prefix: 'SHp',
  },
  [CnxIdType.Carrier]: {
    ...defaultOptions,
    prefix: 'CaR',
  },
  [CnxIdType.Dispatcher]: {
    ...defaultOptions,
    prefix: 'DsP',
  },
  [CnxIdType.Article]: {
    ...defaultOptions,
    prefix: 'ArT',
  },
  [CnxIdType.Order]: {
    ...defaultOptions,
    prefix: 'OrD',
  },
  [CnxIdType.OrderPlanning]: {
    ...defaultOptions,
    prefix: 'OrP',
  },
  [CnxIdType.OrderPlanningVehicle]: {
    ...defaultOptions,
    prefix: 'OpV',
  },
  [CnxIdType.Packaging]: {
    ...defaultOptions,
    prefix: 'PkG',
  },
};

export function createCnxId(cnxIdType: CnxIdType = CnxIdType.Generic) {
  return () => nanoid(cnxIdTypeOptions[cnxIdType]);
}

const nanoid = (options: CnxIdOptions = defaultOptions): string => {
  const prefix = options?.prefix || '';
  const prefixSize = prefix.length;
  const timeSize = options?.time_size ?? 0;
  const time = timeSize ? encodeTime(options.alphabet, timeSize) : '';

  const nanoid = customAlphabet(
    options.alphabet,
    options.size - timeSize - prefixSize,
  );

  return `${prefix}${time}${nanoid()}`;
};

function encodeTime(alphabet: string, len: number): string {
  let now = Date.now();
  let mod;
  let str = '';

  for (; len > 0; len--) {
    mod = now % alphabet.length;
    str = alphabet.charAt(mod) + str;
    now = (now - mod) / alphabet.length;
  }

  return str;
}

export const validateCnxId = (
  cnxId: string,
  cnxIdType: CnxIdType = CnxIdType.Generic,
) => {
  const options = cnxIdTypeOptions[cnxIdType];
  return (
    cnxId.length === options.size && cnxId.startsWith(options.prefix ?? '')
  );
};

export function IsCnxId(
  property: CnxIdType,
  validationOptions?: ValidationOptions,
) {
  // eslint-disable-next-line @typescript-eslint/ban-types
  return function (object: Object, propertyName: string) {
    registerDecorator({
      name: 'isCnxId',
      target: object.constructor,
      propertyName: propertyName,
      constraints: [property],
      options: {
        context: {
          messageKey: 'common.validationErrors.cnxId',
          data: { cnxIdType: property },
        },
        ...validationOptions,
      },
      validator: {
        validate(value: string, args: ValidationArguments) {
          const [cnxIdType] = args.constraints;
          return typeof value === 'string' && validateCnxId(value, cnxIdType);
        },
        defaultMessage(args: ValidationArguments) {
          return `${args.property} is not a valid cnx id`;
        },
      },
    });
  };
}

export const genericId = createCnxId(CnxIdType.Generic);
export const randomId = createCnxId(CnxIdType.Random);
export const shortRandomId = createCnxId(CnxIdType.RandomShort);
export const userId = createCnxId(CnxIdType.User);
export const tenantId = createCnxId(CnxIdType.Tenant);
export const companyId = createCnxId(CnxIdType.Company);
export const locationId = createCnxId(CnxIdType.Location);
export const loadId = createCnxId(CnxIdType.Load);
export const vehicleId = createCnxId(CnxIdType.Vehicle);
export const fleetId = createCnxId(CnxIdType.Fleet);
export const tourPlanFleetId = createCnxId(CnxIdType.TourPlanFleet);
export const tourplanId = createCnxId(CnxIdType.Tourplan);
export const tourId = createCnxId(CnxIdType.Tour);
export const stopId = createCnxId(CnxIdType.Stop);
export const shipmentId = createCnxId(CnxIdType.Shipment);
export const carrierId = createCnxId(CnxIdType.Carrier);
export const dispatcherId = createCnxId(CnxIdType.Dispatcher);
export const articleId = createCnxId(CnxIdType.Article);
export const orderId = createCnxId(CnxIdType.Order);
export const packageId = createCnxId(CnxIdType.Packaging);

// export default { genericId, ... }
